// Play order, as voya.ge's player decides it (voyage/src/main.js pickNext/shuffled, voyage/src/core/composition.js
// loadComposition). No DOM or fetch here, so it can be tested in Node.
//
// Shuffle on (the default; Voyage admin's per-playlist "Shuffle" box, data/playlists.json):
//   first:  a random item (Math.random), or the start item when one is given (voya.ge ?composition=<id>; here the
//           item carried through a door from another site)
//   next:   the other items once each in a Fisher-Yates shuffle; then each new round is a new shuffle of all items,
//           and a round never starts with the item that just played (it is moved to the end of the round)
// Shuffle off: the first item (or the start item), then the next one in playlist order, looping.

/** Admin state in data/playlists.json (voyage/lib/admin.js). */
export interface VoyagePlaylistsState {
  playlists?: { id?: string; shuffle?: boolean }[];
  live?: string | null;
}

/**
 * Voyage's rule (voyage/src/core/composition.js readShuffleSetting): the ?playlist=<id> playlist's setting, else the
 * live playlist's. Missing file, missing entry or missing field = shuffle.
 */
export function shuffleSetting(state: VoyagePlaylistsState | null | undefined, playlistId: string | null): boolean {
  if (!state || !Array.isArray(state.playlists)) return true;
  const id = playlistId ?? state.live;
  return state.playlists.find((p) => p?.id === id)?.shuffle !== false;
}

/** Fisher-Yates shuffle (returns a new array), as voyage/src/main.js shuffled(). */
export function shuffled<T>(list: readonly T[], random: () => number = Math.random): T[] {
  const a = [...list];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}

/** Index order over a list of `count` items. */
export class PlayOrder {
  private current = 0;
  private upcoming: number[] = [];

  constructor(readonly count: number, readonly shuffle: boolean, private random: () => number = Math.random) {}

  /** Index of the first item; `start` = index of a start item (-1 or undefined: none). */
  first(start?: number): number {
    if (this.count <= 0) return -1;
    if (start !== undefined && start >= 0 && start < this.count) this.current = start;
    else this.current = this.shuffle ? Math.floor(this.random() * this.count) : 0;
    const rest = [...Array(this.count).keys()].filter((i) => i !== this.current);
    this.upcoming = this.shuffle ? shuffled(rest, this.random) : [];
    return this.current;
  }

  /** Index of the next item. A one-item list gives the same index again. */
  next(): number {
    if (this.count <= 0) return -1;
    if (!this.shuffle) {
      this.current = (this.current + 1) % this.count;
      return this.current;
    }
    if (!this.upcoming.length) {
      this.upcoming = shuffled([...Array(this.count).keys()], this.random);
      if (this.upcoming.length > 1 && this.upcoming[0] === this.current) this.upcoming.push(this.upcoming.shift()!);
    }
    this.current = this.upcoming.shift()!;
    return this.current;
  }
}
